import { getAccessToken } from "./AccountService";
import config from "../config.json";
import { AccountInfo, IPublicClientApplication } from "@azure/msal-browser";
import Project from "../Models/Project";
import Phase from "../Models/Phase";
import TestType from "../Models/TestType";
import Question from "../Models/Question";
import Tests from "../Models/Tests";
import Assignment from "../Models/Assignment";
import FAQ from "../Models/FAQ";
import RecentActivitys from "../Models/RecentActivity";
import Notificationss from "../Models/Notification";
import UpdateProfilePictureResponse from "../Models/ProfilePictureresponse";
import MyApplicationRole from "../Models/MyApplicationRole";
import Conversations from "../Models/Conversations";
import { Messages } from "../Models/Message";
import {
    CreateRetestPayload,
    ProjectRemediationSeries,
    Retest,
    RetestPhaseFindings,
    RetestResult,
    TrajectoryPoint,
} from "../Models/Retest";



const getFetchOptions = async (
    instance: IPublicClientApplication,
    account: AccountInfo
): Promise<RequestInit> => {
    const accessToken = await getAccessToken(instance, account);

    let headers = new Headers();
    headers.append("Authorization", `Bearer ${accessToken}`);
    headers.append("Content-Type", "application/json");

    return {
        method: "GET",
        headers,
    };
};

/* --------------------------------------------------------------------------
 * Tier-2 caching. A tiny time-based cache that composes with the in-flight
 * de-dup below: a fresh entry is served without a network call; a stale or
 * absent entry falls through to the shared in-flight request and repopulates
 * the cache. Used only for read-heavy, low-churn endpoints (see below).
 * ------------------------------------------------------------------------ */
type CacheEntry<T> = { data: T; at: number };
const freshData = <T>(entry: CacheEntry<T> | null, ttlMs: number): T | null =>
    entry && Date.now() - entry.at < ttlMs ? entry.data : null;

//////Projects////



// In-flight de-dupe: concurrent callers for the same project share one request.
// Tier-3 cache: DashboardPage fans this out over the user's test list (an N+1) and other
// pages fetch the same projects — so cache each result (60s) and clear it on project
// mutations via invalidateProjectCaches(). Reduces repeat/cross-page lookups; the cold
// first load still costs N until the /Tests list itself carries this data (backend change).
const PROJECT_DETAIL_TTL = 60 * 1000;
const inflightProjectDetail = new Map<string, Promise<Project>>();
const projectDetailCache = new Map<string, CacheEntry<Project>>();

export async function getProjectById(
    testid: string,
    id: number,
    instance: IPublicClientApplication,
    account: AccountInfo
): Promise<Project> {
    const key = `${testid}/${id}`;
    const cached = freshData(projectDetailCache.get(key) ?? null, PROJECT_DETAIL_TTL);
    if (cached) return cached;

    const existing = inflightProjectDetail.get(key);
    if (existing) return existing;

    const request = (async (): Promise<Project> => {
        const options = await getFetchOptions(instance, account);

        let response = await fetch(
            `${config.ptaasApi.baseUrl}/Projects/${testid}/${id}`,
            options
        );

        if (response.status === 204 || response.status === 200) {
            const data: Project = await response.json();
            projectDetailCache.set(key, { data, at: Date.now() });
            return data;
        }

        throw new Error();
    })();

    inflightProjectDetail.set(key, request);
    try {
        return await request;
    } finally {
        inflightProjectDetail.delete(key);
    }
}
// In-flight de-dupe: concurrent callers for the same test id share one request.
// Tier-3 cache: ProjectsPage and DashboardPage each fan this out over the test list to
// resolve status + canopy-id per test (an N+1), and both do it independently — so cache
// each result (60s, shared across pages) and clear it on project mutations.
const inflightProjectByTestId = new Map<string, Promise<Project>>();
const projectByTestIdCache = new Map<string, CacheEntry<Project>>();

export async function getProjectByTestId(
    instance: IPublicClientApplication,
    account: AccountInfo,
    id: string
): Promise<Project> {
    const cached = freshData(projectByTestIdCache.get(id) ?? null, PROJECT_DETAIL_TTL);
    if (cached) return cached;

    const existing = inflightProjectByTestId.get(id);
    if (existing) return existing;

    const request = (async (): Promise<Project> => {
        const options = await getFetchOptions(instance, account);

        let response = await fetch(`${config.ptaasApi.baseUrl}/Projects/by-testid/${id}`, options);

        if (response.status === 204 || response.status === 200) {
            const data: Project = await response.json();
            projectByTestIdCache.set(id, { data, at: Date.now() });
            return data;
        }

        throw new Error();
    })();

    inflightProjectByTestId.set(id, request);
    try {
        return await request;
    } finally {
        inflightProjectByTestId.delete(id);
    }
}


// In-flight de-dupe: concurrent callers for the same phase share one request; cleared once settled.
const inflightPhaseById = new Map<string, Promise<Phase>>();

export async function getPhaseById(
    testid: string,
    projectId: number,
    phaseId: number,
    instance: IPublicClientApplication,
    account: AccountInfo
): Promise<Phase> {
    const key = `${testid}/${projectId}/${phaseId}`;
    const existing = inflightPhaseById.get(key);
    if (existing) return existing;

    const request = (async (): Promise<Phase> => {
        const options = await getFetchOptions(instance, account);

        const response = await fetch(
            `${config.ptaasApi.baseUrl}/Projects/${testid}/${projectId}/phases/${phaseId}`,
            options
        );

        if (response.status === 204 || response.status === 200)
            return await response.json();

        throw new Error();
    })();

    inflightPhaseById.set(key, request);
    try {
        return await request;
    } finally {
        inflightPhaseById.delete(key);
    }
}

export async function getPdfFromHtml(
    projectId: number,
    phaseId: number,
    testid: string,
    instance: IPublicClientApplication,
    account: AccountInfo
): Promise<Blob> {
    const options = await getFetchOptions(instance, account);

    options.method = "GET";

    let url = `${config.ptaasApi.baseUrl}/documents/report-pdf?projectId=${projectId}&testid=${testid}`;

    if (phaseId !== null) url += `&phaseId=${phaseId}`;

    const response = await fetch(url, options);

    if (response.ok) return await response.blob();

    const errorMessage = await response.text();

    throw new Error(`${response.status}: ${errorMessage}`);
}


/* ---------------- */
/*     TestType     */
/* ---------------- */

// In-flight de-dupe: the test-type catalog is global and is requested concurrently by
// several per-test-type child components mounting on the same page. Share one /TestTypes
// request instead of N.
// Tier-2 cache: it's reference data that changes very rarely, so also cache it across
// navigation for the session (10-minute TTL) instead of refetching on every mount.
let inflightTestTypes: Promise<TestType[]> | null = null;
const TEST_TYPES_TTL = 10 * 60 * 1000;
let testTypesCache: CacheEntry<TestType[]> | null = null;

export async function getTestType(
    instance: IPublicClientApplication,
    account: AccountInfo
): Promise<TestType[]> {
    const cached = freshData(testTypesCache, TEST_TYPES_TTL);
    if (cached) return cached;

    if (inflightTestTypes) return inflightTestTypes;

    inflightTestTypes = (async (): Promise<TestType[]> => {
        const options = await getFetchOptions(instance, account);
        const response = await fetch(`${config.ptaasApi.baseUrl}/TestTypes`, options);
        if (response.status === 204 || response.status === 200) {
            const data: TestType[] = await response.json();
            testTypesCache = { data, at: Date.now() };
            return data;
        }
        throw new Error();
    })();

    try {
        return await inflightTestTypes;
    } finally {
        inflightTestTypes = null;
    }
}

/* ---------------- */
/* Scoping Question */
/* ---------------- */


// In-flight de-dupe: concurrent callers for the same question id share one request; cleared once settled.
const inflightQuestionsByQuestionId = new Map<string, Promise<Question[]>>();

export async function getQuestionsByQuestionId(
    instance: IPublicClientApplication,
    account: AccountInfo,
    id: string
): Promise<Question[]> {
    const existing = inflightQuestionsByQuestionId.get(id);
    if (existing) return existing;

    const request = (async (): Promise<Question[]> => {
        const options = await getFetchOptions(instance, account);
        options.method = "GET";
        let response = await fetch(`${config.ptaasApi.baseUrl}/ByQuestionId/${id}`, options);
        if (response.status === 204 || response.status === 200) return await response.json();

        throw new Error();
    })();

    inflightQuestionsByQuestionId.set(id, request);
    try {
        return await request;
    } finally {
        inflightQuestionsByQuestionId.delete(id);
    }
}




/* ------------------------*/
/* PreReq Question Section */
/* ------------------------ */


// In-flight de-dupe: prereqs for the same question id are fetched concurrently by several
// components (ProjectPreReqs loops assignments, SetupForm, etc.). Share one request per id.
const inflightPrereqByQuestion = new Map<string, Promise<Question[]>>();

export async function getPrereqQuestionByQuestionId(

    instance: IPublicClientApplication,
    account: AccountInfo,
    id: string
): Promise<Question[]> {
    const existing = inflightPrereqByQuestion.get(id);
    if (existing) return existing;

    const request = (async (): Promise<Question[]> => {
        const options = await getFetchOptions(instance, account);
        const response = await fetch(`${config.ptaasApi.baseUrl}/PrereqQuestion/${id}`, options);
        if (response.status === 204 || response.status === 200) return await response.json();
        throw new Error();
    })();

    inflightPrereqByQuestion.set(id, request);
    try {
        return await request;
    } finally {
        inflightPrereqByQuestion.delete(id);
    }
}


/* -------------------------------------*/
/* Creating New Project Request Section */
/* ------------------------------------ */

export async function postnewproject
    (
        instance: IPublicClientApplication,
        account: AccountInfo,
        projectname: string,
        projectowner: string,
        owneremail: string,
        status: string,
        words2: string,
        assignment: Assignment[]
    ): Promise<boolean> {
    const options = await getFetchOptions(instance, account);
    options.method = "POST";
    const testtype = words2.split(',');
    options.body = JSON.stringify({
        projectname, projectowner, owneremail, status, testtype, assignment
    });
    let response = await fetch(`${config.ptaasApi.baseUrl}/Tests`, options);

    if (response.status === 204 || response.status === 200) { invalidateProjectCaches(); return await response.ok; }
    const errorMessage = await response.text();

    throw new Error(`${response.status}: ${errorMessage}`);
}

// In-flight de-dupe: the /Tests list can be fetched more than once when an effect
// re-runs on a quick re-render (page + widgets mounting together). Concurrent callers
// share one request.
// Tier-2 cache: this list is read on almost every page — including the always-mounted
// breadcrumb — so cache it briefly (60s) to stop SPA navigation refetching it. The cache
// is cleared explicitly by project mutations (create / edit / repeat / quote-sign) via
// invalidateProjectCaches(); the short TTL is a backstop for server-side status changes.
// Cache hits return a copy so an in-place consumer sort can't reorder the stored list.
let inflightProjectsForUser: Promise<Tests[]> | null = null;
const PROJECTS_FOR_USER_TTL = 60 * 1000;
let projectsForUserCache: CacheEntry<Tests[]> | null = null;

// The enriched list from GET /Tests/summary: each test plus its resolved canopy project id
// and raw Jira project status. Lets ProjectsPage/DashboardPage skip the per-test
// getProjectByTestId fan-out entirely.
export interface TestSummary extends Tests {
    canopyProjectId: number | null;
    projectStatus: string | null;
}

// Same Tier-2 cache + in-flight de-dup as getprojectforuser, but for the heavier enriched
// endpoint. Cleared by the same mutations via invalidateProjectCaches().
let inflightTestsSummary: Promise<TestSummary[]> | null = null;
let testsSummaryCache: CacheEntry<TestSummary[]> | null = null;

// Clear every cached view of the user's projects — the plain list, the enriched summary, and
// both per-project detail caches — after any mutation that can change project data. Coarse but
// safe: mutations are infrequent, and over-clearing only costs a refetch.
export function invalidateProjectCaches(): void {
    projectsForUserCache = null;
    testsSummaryCache = null;
    projectByTestIdCache.clear();
    projectDetailCache.clear();
}

export async function getTestsSummary(
    instance: IPublicClientApplication,
    account: AccountInfo
): Promise<TestSummary[]> {
    const cached = freshData(testsSummaryCache, PROJECTS_FOR_USER_TTL);
    if (cached) return cached.slice();

    if (inflightTestsSummary) return inflightTestsSummary;

    const request = (async (): Promise<TestSummary[]> => {
        const options = await getFetchOptions(instance, account);
        options.method = "GET";

        let response = await fetch(`${config.ptaasApi.baseUrl}/Tests/summary`, options);

        if (response.status === 204 || response.status === 200) {
            const data: TestSummary[] = await response.json();
            testsSummaryCache = { data, at: Date.now() };
            return data;
        }
        throw new Error();
    })();

    inflightTestsSummary = request;
    try {
        return await request;
    } finally {
        inflightTestsSummary = null;
    }
}

export async function getprojectforuser(
    instance: IPublicClientApplication,
    account: AccountInfo
): Promise<Tests[]> {
    const cached = freshData(projectsForUserCache, PROJECTS_FOR_USER_TTL);
    if (cached) return cached.slice();

    if (inflightProjectsForUser) return inflightProjectsForUser;

    const request = (async (): Promise<Tests[]> => {
        const options = await getFetchOptions(instance, account);
        options.method = "GET";

        let response = await fetch(`${config.ptaasApi.baseUrl}/Tests`, options);

        if (response.status === 204 || response.status === 200) {
            const data: Tests[] = await response.json();
            projectsForUserCache = { data, at: Date.now() };
            return data;
        }
        throw new Error();
    })();

    inflightProjectsForUser = request;
    try {
        return await request;
    } finally {
        inflightProjectsForUser = null;
    }
}

// In-flight de-dupe: concurrent callers for the same project id (page + setup form /
// question list mounting together) share a single /Tests/{id} request instead of each
// firing their own. Only overlapping in-flight calls are merged (cleared once settled),
// so later/sequential calls stay fresh.
const inflightProjectById = new Map<string, Promise<Tests>>();

export async function getprojectbyidforuser(
    instance: IPublicClientApplication,
    account: AccountInfo,
    id: string
): Promise<Tests> {
    const existing = inflightProjectById.get(id);
    if (existing) return existing;

    const request = (async (): Promise<Tests> => {
        const options = await getFetchOptions(instance, account);
        options.method = "GET";
        let response = await fetch(`${config.ptaasApi.baseUrl}/Tests/${id}`, options);
        if (!response.ok) {
            // Create error object with status for proper handling
            const error: any = new Error(`HTTP ${response.status}: ${response.statusText}`);
            error.status = response.status;
            error.statusText = response.statusText;

            // Try to get error message from response body
            try {
                const errorData = await response.json();
                error.message = errorData.message || errorData.error || error.message;
            } catch {
                error.message = response.statusText || error.message;
            }

            throw error;
        }

        return await response.json();
    })();

    inflightProjectById.set(id, request);
    try {
        return await request;
    } finally {
        inflightProjectById.delete(id);
    }
};

export interface RepeatAssessmentRequestPayload {
    noSignificantChangesConfirmed: boolean;
    newProjectName: string;
    requestedStartDate: string;
    requestedEndDate: string;
    comments?: string;
}

export interface RepeatAssessmentResponse {
    sourceProjectId: string;
    newProjectId: string;
    newProjectTicketNo: string;
    newProjectName: string;
    quoteCreated: boolean;
    quotePrepopulatedFromPrevious: boolean;
    pendingActions: string[];
}

export async function createRepeatAssessment(
    instance: IPublicClientApplication,
    account: AccountInfo,
    sourceProjectId: string,
    payload: RepeatAssessmentRequestPayload
): Promise<RepeatAssessmentResponse> {
    const options = await getFetchOptions(instance, account);
    options.method = "POST";
    options.body = JSON.stringify(payload);

    const response = await fetch(`${config.ptaasApi.baseUrl}/Tests/${sourceProjectId}/repeat-assessment`, options);

    if (response.status === 200 || response.status === 201) {
        invalidateProjectCaches();
        return await response.json();
    }

    const errorMessage = await response.text();
    throw new Error(errorMessage || "Failed to create repeat assessment.");
}

/* ---------------- */
/*      Retest      */
/* ---------------- */

export async function getEligibleRetestFindings(
    instance: IPublicClientApplication,
    account: AccountInfo,
    projectId: string
): Promise<RetestPhaseFindings[]> {
    const options = await getFetchOptions(instance, account);
    options.method = "GET";

    const response = await fetch(`${config.ptaasApi.baseUrl}/Tests/${projectId}/retests/eligible-findings`, options);

    if (response.status === 200) {
        return await response.json();
    }

    // 502 means Canopy could not be reached. An empty pool and an unreachable Canopy must
    // never look the same to the user, so surface the message rather than returning [].
    throw new Error(await response.text());
}

export async function createRetest(
    instance: IPublicClientApplication,
    account: AccountInfo,
    projectId: string,
    payload: CreateRetestPayload
): Promise<RetestResult> {
    const options = await getFetchOptions(instance, account);
    options.method = "POST";
    options.body = JSON.stringify(payload);

    const response = await fetch(`${config.ptaasApi.baseUrl}/Tests/${projectId}/retests`, options);

    if (response.status === 200 || response.status === 201) {
        return await response.json();
    }

    throw new Error(await response.text());
}

export async function getRetests(
    instance: IPublicClientApplication,
    account: AccountInfo,
    projectId: string
): Promise<Retest[]> {
    const options = await getFetchOptions(instance, account);
    options.method = "GET";

    const response = await fetch(`${config.ptaasApi.baseUrl}/Tests/${projectId}/retests`, options);

    if (response.status === 200) {
        return await response.json();
    }

    throw new Error(await response.text());
}

export async function getRetestTrajectory(
    instance: IPublicClientApplication,
    account: AccountInfo,
    projectId: string
): Promise<TrajectoryPoint[]> {
    const options = await getFetchOptions(instance, account);
    options.method = "GET";

    const response = await fetch(`${config.ptaasApi.baseUrl}/Tests/${projectId}/retests/trajectory`, options);

    if (response.status === 200) {
        return await response.json();
    }

    throw new Error(await response.text());
}

// Dashboard/remediation-trajectory (spec §9.2): no projectId parameter -- the project
// set is derived server-side from the caller's own ClientId, so one client can never
// read another's posture through this endpoint.
export async function getRemediationTrajectory(
    instance: IPublicClientApplication,
    account: AccountInfo
): Promise<ProjectRemediationSeries[]> {
    const options = await getFetchOptions(instance, account);
    options.method = "GET";

    const response = await fetch(`${config.ptaasApi.baseUrl}/Dashboard/remediation-trajectory`, options);

    if (response.status === 200) {
        return await response.json();
    }

    throw new Error(await response.text());
}

export async function rejectRetestQuote(
    instance: IPublicClientApplication,
    account: AccountInfo,
    retestId: string
): Promise<void> {
    const options = await getFetchOptions(instance, account);
    options.method = "POST";

    const response = await fetch(`${config.ptaasApi.baseUrl}/retests/${retestId}/reject-quote`, options);

    if (response.status !== 200) {
        throw new Error(await response.text());
    }
}

export async function undoRetestRiskAccept(
    instance: IPublicClientApplication,
    account: AccountInfo,
    retestId: string,
    findingUuid: string
): Promise<void> {
    const options = await getFetchOptions(instance, account);
    options.method = "POST";

    const response = await fetch(
        `${config.ptaasApi.baseUrl}/retests/${retestId}/findings/${findingUuid}/undo-risk-accept`,
        options
    );

    if (response.status !== 200) {
        throw new Error(await response.text());
    }
}

export async function putprojectdetails(
    instance: IPublicClientApplication,
    account: AccountInfo,
    id: string,
    status: string,
    projectname: string,
    projectowner: string,
    owneremail: string,
    accepteddate: string,
    availabledate: Date[],
    comment: string,
    testType: string[],
    assignment: Assignment[]
)
    : Promise<Tests | null> {
    const options = await getFetchOptions(instance, account);
    options.method = "PUT";
    options.body = JSON.stringify(
        {
            "projectName": projectname,
            "projectOwner": projectowner,
            "owneremail": owneremail,
            "status": status,
            "testType": testType,
            "comment": comment,
            "assignment": assignment
        }
    );

    let response = await fetch(`${config.ptaasApi.baseUrl}/Tests/${id}`, options);
    if (!response.ok) {
        // Log or handle error more specifically here
        throw new Error(`API request failed with status ${response.status}: ${response.statusText}`);
    }

    // Project details (name/status/etc.) may have changed — drop the cached list.
    invalidateProjectCaches();

    // Since your API always returns OK, we check for the presence of content before parsing
    const text = await response.text(); // First get response as text
    if (!text) {
        // Handle case where no response body is present
        return null;
    }

    try {
        return JSON.parse(text); // Parse text as JSON
    } catch (error) {
        throw new Error('Failed to parse JSON response');
    }

}




/* -------------------------------------*/
/*         Quotes Section            */
/* ------------------------------------ */




export async function QuoteApproval(
    instance: IPublicClientApplication,
    account: AccountInfo,
    testId: string,
    name: string,
    date: string,
    position: string,
    isSigned: boolean,
    signature: string,
    accountsContactName?: string,
    accountsEmail?: string,
    purchaseOrderNumber?: string
): Promise<boolean> {
    const options = await getFetchOptions(instance, account);
    options.method = "POST";
    options.body = JSON.stringify({
        testId,
        name,
        date,
        position,
        isSigned,
        signature,
        accountsContactName,
        accountsEmail,
        purchaseOrderNumber
    });
    const response = await fetch(`${config.ptaasApi.baseUrl}/QuotationSignature`, options);
    if (!response.ok) {
        const errorMessage = await response.text();
        throw new Error(`${response.status}: ${errorMessage}`);
    }
    // Signing changes the project's quote status shown in the list — drop the cache.
    invalidateProjectCaches();
    return true;
}
// A quote's signature state and where its PDF lives, without downloading the PDF itself. Cheap
// enough to ask for every quote on a page — the download is the expensive half.
export async function getQuotePdfInfo(
    testid: string,
    instance: IPublicClientApplication,
    account: AccountInfo
): Promise<{ isSigned: boolean; fileUrl: string }> {
    const options = await getFetchOptions(instance, account);
    options.method = "GET";

    const url = `${config.ptaasApi.baseUrl}/documents/quote-pdf-info?testId=${testid}`;
    const response = await fetch(url, options);

    if (!response.ok) {
        const errorMessage = await response.text();
        throw new Error(`${response.status}: ${errorMessage}`);
    }

    const data = await response.json();

    if (!data.fileUrl) {
        throw new Error("File URL is missing in the response");
    }

    return { isSigned: data.isSigned, fileUrl: data.fileUrl };
}

export async function getQuotePdfFromHtml(
    testid: string,
    instance: IPublicClientApplication,
    account: AccountInfo
): Promise<{ isSigned: boolean; pdfBlob: Blob }> {
    const options = await getFetchOptions(instance, account);
    options.method = "GET";

    // Step 1: Fetch quote PDF info (contains isSigned & fileUrl)
    const data = await getQuotePdfInfo(testid, instance, account);

    // Step 2: Fetch the actual PDF file
    const pdfResponse = await fetch(data.fileUrl, options);

    if (!pdfResponse.ok) {
        throw new Error(`Failed to download PDF: ${pdfResponse.status}`);
    }

    const pdfBlob = await pdfResponse.blob();

    return { isSigned: data.isSigned, pdfBlob };
}



/* -------------------------------------*/
/*         FAQs Section            */
/* ------------------------------------ */
// In-flight de-dupe: the FAQs list can be fetched more than once when an effect
// re-runs on a quick re-render. Concurrent callers share one request; cleared once settled.
let inflightFAQs: Promise<FAQ[]> | null = null;

export async function getFAQs
    (
        instance: IPublicClientApplication,
        account: AccountInfo,
    ): Promise<FAQ[]> {
    if (inflightFAQs) return inflightFAQs;

    const request = (async (): Promise<FAQ[]> => {
        const options = await getFetchOptions(instance, account);
        options.method = "GET";
        let response = await fetch(`${config.ptaasApi.baseUrl}/FAQs`, options);
        if (response.status === 204 || response.status === 200) return await response.json();

        throw new Error();
    })();

    inflightFAQs = request;
    try {
        return await request;
    } finally {
        inflightFAQs = null;
    }
}


/* -------------------------------------*/
/*         Messaging Section            */
/* ------------------------------------ */
// In-flight de-dupe: the /Messaging list can be fetched more than once when an effect
// re-runs on a quick re-render. Concurrent callers share one request; cleared once settled.
let inflightMessagesFromJira: Promise<Messages[]> | null = null;

export async function getMessagesFromJira
    (
        instance: IPublicClientApplication,
        account: AccountInfo,
    ): Promise<Messages[]> {
    if (inflightMessagesFromJira) return inflightMessagesFromJira;

    const request = (async (): Promise<Messages[]> => {
        const options = await getFetchOptions(instance, account);
        options.method = "GET";
        let response = await fetch(`${config.ptaasApi.baseUrl}/Messaging`, options);
        if (response.status === 204 || response.status === 200) return await response.json();

        throw new Error();
    })();

    inflightMessagesFromJira = request;
    try {
        return await request;
    } finally {
        inflightMessagesFromJira = null;
    }
}
export async function CreateConversation(
    instance: IPublicClientApplication,
    account: AccountInfo,
    title: string,
    jiraIssueKey: string
): Promise<string> {
    const options = await getFetchOptions(instance, account);
    options.method = "POST";
    options.body = JSON.stringify({ title, jiraIssueKey });
    try {
        const response = await fetch(`${config.ptaasApi.baseUrl}/Messaging/conversations`, options);

        if (response.status === 200) {
            return await response.text();
        } else if (response.status === 204) {
            return await response.text(); //  An empty array of messages
        } else {
            // Handle other unexpected statuses
            if (!response.ok) {
                throw new Error(`Failed to fetch Id: Server returned status ${response.status}`);
            }
        }
    } catch (error) {
        // Log or handle errors more specifically if needed
        console.error('Error fetching Id from Jira:', error);
        throw new Error('There was an creating an chat window. Please try again later.');
    }
}
// In-flight de-dupe: the conversations list can be fetched more than once when an effect
// re-runs on a quick re-render. Concurrent callers share one request; cleared once settled.
let inflightConversations: Promise<Conversations[]> | null = null;

export async function GetConversations(
    instance: IPublicClientApplication,
    account: AccountInfo
): Promise<Conversations[]> {
    if (inflightConversations) return inflightConversations;

    const request = (async (): Promise<Conversations[]> => {
        const options = await getFetchOptions(instance, account);
        options.method = "GET";

        try {
            const response = await fetch(`${config.ptaasApi.baseUrl}/Messaging/conversations`, options);

            if (response.status === 200) {

                return await response.json();
            } else if (response.status === 204 || response.status === 404) {

                return [];
            } else {

                throw new Error(`Failed to fetch conversations: Server returned status ${response.status}`);
            }

        } catch (error) {
            console.error('Error fetching conversations:', error);
            throw new Error('There was an error retrieving conversations. Please try again later.');
        }
    })();

    inflightConversations = request;
    try {
        return await request;
    } finally {
        inflightConversations = null;
    }
}

// In-flight de-dupe: concurrent callers for the same conversation share one request; cleared once settled.
const inflightMessagesByConversation = new Map<string, Promise<Messages[]>>();

export async function GetMessages(
    instance: IPublicClientApplication,
    account: AccountInfo,
    conversationid: string
): Promise<Messages[]> {
    const existing = inflightMessagesByConversation.get(conversationid);
    if (existing) return existing;

    const request = (async (): Promise<Messages[]> => {
        const options = await getFetchOptions(instance, account);
        options.method = "GET";
        try {
            const response = await fetch(`${config.ptaasApi.baseUrl}/Messaging/conversations/${conversationid}`, options);

            if (response.status === 200) {
                return await response.json();
            } else if (response.status === 204 || response.status === 404) {

                return [];
            } else {
                // Handle other unexpected statuses
                throw new Error(`Failed to fetch Id: Server returned status ${response.status}`);
            }
        } catch (error) {
            throw new Error('There was an error fetching messages');
        }
    })();

    inflightMessagesByConversation.set(conversationid, request);
    try {
        return await request;
    } finally {
        inflightMessagesByConversation.delete(conversationid);
    }
}


// In-flight de-dupe: concurrent callers for the same parent id share one request; cleared once settled.
const inflightChatByParent = new Map<string, Promise<string>>();

export async function GetChatByTestId(
    instance: IPublicClientApplication,
    account: AccountInfo,
    parentid: string,
): Promise<string> {
    const existing = inflightChatByParent.get(parentid);
    if (existing) return existing;

    const request = (async (): Promise<string> => {
        const options = await getFetchOptions(instance, account);
        options.method = "GET";
        try {
            const response = await fetch(`${config.ptaasApi.baseUrl}/Messaging/${parentid}`, options);

            if (response.status === 200) {
                return await response.text();
            } else if (response.status === 204) {
                return await response.text(); //  An empty array of messages
            } else {
                // Handle other unexpected statuses
                throw new Error(`Failed to fetch Id: Server returned status ${response.status}`);
            }
        } catch (error) {
            // Log or handle errors more specifically if needed
            console.error('Error fetching Id from Jira:', error);
            throw new Error('There was an creating an chat window. Please try again later.');
        }
    })();

    inflightChatByParent.set(parentid, request);
    try {
        return await request;
    } finally {
        inflightChatByParent.delete(parentid);
    }
}



export async function postMessageInConversation(
    instance: IPublicClientApplication,
    account: AccountInfo,
    conversationId: string,
    body: string,
    file: File | null,
    idempotencyKey: string
): Promise<Messages> {
    const options = await getFetchOptions(instance, account);
    options.method = "POST";
    

    const formData = new FormData();
    formData.append("body", body);
    formData.append("idempotencyKey", idempotencyKey);
    if (file) {
        formData.append("file", file, file.name);
    }
    options.body = formData;
     if (options.headers) {
        if (options.headers instanceof Headers) {
            options.headers.delete('Content-Type'); // Using Headers API to delete
        } else {
            delete options.headers['Content-Type']; // For plain object headers
        }
    }

    try {
        const response = await fetch(
            `${config.ptaasApi.baseUrl}/Messaging/conversations/${conversationId}/messages`,
            options
        );

        if (response.status === 201) { // Changed to 201 for Created
            return await response.json();
        } else if (response.status === 200) {
            return await response.json();
        } else {
            const errorText = await response.text();
            throw new Error(`Failed to post message: ${response.status} - ${errorText}`);
        }
    } catch (error) {
        console.error('Error posting message:', error);
        throw error;
    }
}




export const CloseConversation = async (
    instance: IPublicClientApplication,
    account: AccountInfo,
    conversationId: string
): Promise<void> => {
    const options = await getFetchOptions(instance, account);
    options.method = "PUT";
    const response = await fetch(`${config.ptaasApi.baseUrl}/Messaging/conversations/${conversationId}/close`, options);

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to close conversation');
    }
};


export const DeleteConversation = async (
    instance: IPublicClientApplication,
    account: AccountInfo,
    conversationId: string
): Promise<void> => {
    const options = await getFetchOptions(instance, account);
    options.method = "DELETE";
    const response = await fetch(`${config.ptaasApi.baseUrl}/Messaging/conversations/${conversationId}`, options);
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete conversation');
    }
};




/* -------------------------------------*/
/*      User Preference Section         */
/* ------------------------------------ */
// In-flight de-dupe: /me/details is loaded from a global context on every page and can
// fire more than once on a quick re-render. Concurrent callers share one request; cleared once settled.
let inflightMyPreferenceDetails: Promise<User> | null = null;

export async function getMyPreferenceDetails(
    instance: IPublicClientApplication,
    account: AccountInfo,
): Promise<User> {
    if (inflightMyPreferenceDetails) return inflightMyPreferenceDetails;

    const request = (async (): Promise<User> => {
        const options = await getFetchOptions(instance, account);
        options.method = "GET";

        let response = await fetch(`${config.ptaasApi.baseUrl}/me/details`, options);
        if (response.status === 204 || response.status === 200) return await response.json();

        throw new Error();
    })();

    inflightMyPreferenceDetails = request;
    try {
        return await request;
    } finally {
        inflightMyPreferenceDetails = null;
    }
}

// In-flight de-dupe: /me/profile-picture is loaded from a global context on every page and can
// fire more than once on a quick re-render. Concurrent callers share one request; cleared once settled.
let inflightMyProfilePicture: Promise<Blob> | null = null;

export async function getMyProfilePicture(
    instance: IPublicClientApplication,
    account: AccountInfo,
): Promise<Blob> {
    if (inflightMyProfilePicture) return inflightMyProfilePicture;

    const request = (async (): Promise<Blob> => {
        const options = await getFetchOptions(instance, account);
        options.method = "GET";

        let response = await fetch(`${config.ptaasApi.baseUrl}/me/profile-picture`, options);
        if (response.status === 204 || response.status === 200) return await response.blob();

        throw new Error();
    })();

    inflightMyProfilePicture = request;
    try {
        return await request;
    } finally {
        inflightMyProfilePicture = null;
    }
}

export async function updateMyProfilePicture(
    instance: IPublicClientApplication,
    account: AccountInfo,
    file: File
): Promise<UpdateProfilePictureResponse> {
    const formData = new FormData();
    formData.append('profilePic', file);
    const options = await getFetchOptions(instance, account);
    options.method = "POST";
    options.body = formData;

    if (options.headers) {
        if (options.headers instanceof Headers) {
            options.headers.delete('Content-Type');
        } else {
            delete options.headers['Content-Type'];
        }
    }

    try {
        let response = await fetch(`${config.ptaasApi.baseUrl}/me/profile-picture`, options);

        if (response.ok) {
            const blob = await response.blob();
            const newImageUrl = URL.createObjectURL(blob);
            return {
                success: true,
                message: 'Profile picture updated successfully.',
                newImageUrl: newImageUrl
            };
        } else {
            const errorResponse = await response.json();
            throw new Error(errorResponse.message || 'Failed to update the profile picture.');
        }
    } catch (error) {
        console.error('Error updating profile picture:', error);
        return {
            success: false,
            message: error.message || 'Unknown error occurred.'
        };
    }
}

export async function deleteMyProfilePicture(
    instance: IPublicClientApplication,
    account: AccountInfo,
): Promise<UpdateProfilePictureResponse> {
    const options = await getFetchOptions(instance, account);
    options.method = "DELETE";
    try {
        let response = await fetch(`${config.ptaasApi.baseUrl}/me/profile-picture`, options);
        if (response.ok) {
            return {
                success: true,
                message: 'Profile picture deleted successfully.'
            };
        } else {
            throw new Error('Failed to delete the profile picture.');
        }
    } catch (error) {
        console.error('Error deleting profile picture:', error);
        return {
            success: false,
            message: error.message || 'Unknown error occurred.'
        };
    }
}
// In-flight de-dupe: /me/MaxPrivilegeUserRole is loaded from RoleContext on every page and can
// fire more than once on a quick re-render. Concurrent callers share one request; cleared once settled.
let inflightMyHighestRole: Promise<string> | null = null;

export async function getMyHighestPriviledgeRole(instance: IPublicClientApplication,
    account: AccountInfo,
): Promise<string> {
    if (inflightMyHighestRole) return inflightMyHighestRole;

    const request = (async (): Promise<string> => {
        const options = await getFetchOptions(instance, account);
        options.method = "GET";

        let response = await fetch(`${config.ptaasApi.baseUrl}/me/MaxPrivilegeUserRole`, options);
        if (response.status === 204 || response.status === 200) return await response.text();

        throw new Error();
    })();

    inflightMyHighestRole = request;
    try {
        return await request;
    } finally {
        inflightMyHighestRole = null;
    }
}
// In-flight de-dupe: /me/UserRole is loaded from RoleContext on every page and can
// fire more than once on a quick re-render. Concurrent callers share one request; cleared once settled.
let inflightMyRole: Promise<MyApplicationRole[]> | null = null;

export async function getMyRole(instance: IPublicClientApplication,
    account: AccountInfo,
): Promise<MyApplicationRole[]> {
    if (inflightMyRole) return inflightMyRole;

    const request = (async (): Promise<MyApplicationRole[]> => {
        const options = await getFetchOptions(instance, account);
        options.method = "GET";

        let response = await fetch(`${config.ptaasApi.baseUrl}/me/UserRole`, options);
        if (response.status === 204 || response.status === 200) return await response.json();

        throw new Error();
    })();

    inflightMyRole = request;
    try {
        return await request;
    } finally {
        inflightMyRole = null;
    }
}

/* -------------------------------------*/
/*      Notification   Section         */
/* ------------------------------------ */
// In-flight de-dupe: /Notifications is loaded from a global context on every page and can
// fire more than once on a quick re-render. Concurrent callers share one request; cleared once settled.
let inflightNotifications: Promise<Notificationss[]> | null = null;

export async function GetNotification
    (
        instance: IPublicClientApplication,
        account: AccountInfo,


    ): Promise<Notificationss[]> {
    if (inflightNotifications) return inflightNotifications;

    const request = (async (): Promise<Notificationss[]> => {
        const options = await getFetchOptions(instance, account);
        options.method = "GET";

        let response = await fetch(`${config.ptaasApi.baseUrl}/Notifications`, options);
        if (response.status === 204 || response.status === 200) return await response.json();

        throw new Error();
    })();

    inflightNotifications = request;
    try {
        return await request;
    } finally {
        inflightNotifications = null;
    }
}

export async function UpdateNotification
    (
        instance: IPublicClientApplication,
        account: AccountInfo,
        id?: string
    ): Promise<boolean> {
    const options = await getFetchOptions(instance, account);
    options.method = "PUT";

    const url = id ? `${config.ptaasApi.baseUrl}/Notifications/${id}` : `${config.ptaasApi.baseUrl}/Notifications`;

    let response = await fetch(url, options);
    if (response.ok) {  // Checking if status is 200-299
        return response.json();  // Assuming the API returns a boolean in JSON format
    }

    throw new Error(`Failed to update Notification, status code: ${response.status}`);
}

/* -------------------------------------*/
/*      Recent Activity   Section         */
/* ------------------------------------ */
// In-flight de-dupe: RecentActivity can be fetched more than once when an effect
// re-runs on a quick re-render. Concurrent callers share one request; cleared once settled.
let inflightRecentActivity: Promise<RecentActivitys[]> | null = null;

export async function GetRecentActivity
    (
        instance: IPublicClientApplication,
        account: AccountInfo,


    ): Promise<RecentActivitys[]> {
    if (inflightRecentActivity) return inflightRecentActivity;

    const request = (async (): Promise<RecentActivitys[]> => {
        const options = await getFetchOptions(instance, account);
        options.method = "GET";

        let response = await fetch(`${config.ptaasApi.baseUrl}/RecentActivity`, options);
        if (response.status === 204 || response.status === 200) return await response.json();

        throw new Error();
    })();

    inflightRecentActivity = request;
    try {
        return await request;
    } finally {
        inflightRecentActivity = null;
    }
}

export async function UpdateRecentActivity
    (
        instance: IPublicClientApplication,
        account: AccountInfo,
        id?: string


    ): Promise<boolean> {
    const options = await getFetchOptions(instance, account);
    options.method = "PUT";

    const url = id ? `${config.ptaasApi.baseUrl}/RecentActivity/${id}` : `${config.ptaasApi.baseUrl}/RecentActivity`;

    let response = await fetch(url, options);
    if (response.ok) {  // Checking if status is 200-299
        return response.json();  // Assuming the API returns a boolean in JSON format
    }

    throw new Error(`Failed to update recent activity, status code: ${response.status}`);
}
/* -------------------------------------*/
/*      Sharepoint   Section         */
/* ------------------------------------ */

export async function UploadFiles
    (
        instance: IPublicClientApplication,
        account: AccountInfo,
        id: string,
        file: File,
        spid: string,
        isScoping: boolean


    ): Promise<FileEntryDetails> {
    const formData = new FormData();
    formData.append('file', file);

    const options = await getFetchOptions(instance, account);
    options.method = "POST";
    options.body = formData;
    if (options.headers) {
        if (options.headers instanceof Headers) {
            options.headers.delete('Content-Type'); // Using Headers API to delete
        } else {
            delete options.headers['Content-Type']; // For plain object headers
        }
    }
    let response = await fetch(`${config.ptaasApi.baseUrl}/FileUpload/${id}?spid=${spid}&isScoping=${isScoping}`, options);
    if (response.status === 204 || response.status === 200) { return response.json(); };

    throw new Error();
}

// In-flight de-dupe: concurrent callers for the same project's files share one request; cleared once settled.
const inflightFilesByTicket = new Map<string, Promise<FileEntryDetails[]>>();

export async function getFilesByProjectTicketnumber
    (
        instance: IPublicClientApplication,
        account: AccountInfo,
        id: string
    ): Promise<FileEntryDetails[]> {
    const existing = inflightFilesByTicket.get(id);
    if (existing) return existing;

    const request = (async (): Promise<FileEntryDetails[]> => {
        const options = await getFetchOptions(instance, account);
        options.method = "GET";

        let response = await fetch(`${config.ptaasApi.baseUrl}/FileUpload/${id}`, options);
        if (response.status === 204 || response.status === 200) { return response.json(); };

        throw new Error();
    })();

    inflightFilesByTicket.set(id, request);
    try {
        return await request;
    } finally {
        inflightFilesByTicket.delete(id);
    }
}



export async function Savefeedback(
    instance: IPublicClientApplication,
    account: AccountInfo,
    id: string,
    description: string,
    rating: number
): Promise<void> {
    // Ensure full description includes description and rating
    const fullDescription = `${description}\nRating is ${rating}`;

    // Generate fetch options
    const options = await getFetchOptions(instance, account);
    options.method = "PUT";
    options.body = JSON.stringify({ "Id": id, "Description": fullDescription });
    // Make the API request
    let response = await fetch(`${config.ptaasApi.baseUrl}/Feedback`, options);

    // Check for errors in response
    if (!response.ok) {
        // Log or handle error more specifically here
        throw new Error(`API request failed with status ${response.status}: ${response.statusText}`);
    }
}
export async function uploadprereqinSharepoint(
    instance: IPublicClientApplication,
    account: AccountInfo,
    id: string,
    isScoping: boolean
): Promise<void> {
    const options = await getFetchOptions(instance, account);
    options.method = "POST";


    let response = await fetch(`${config.ptaasApi.baseUrl}/FileUpload/UploadDataInTextFile?id=${id}&isScoping=${isScoping}`, options);
    if (!response.ok) {
        // Log or handle error more specifically here
        throw new Error(`API request failed with status ${response.status}: ${response.statusText}`);
    }
}


